import { useState } from 'react';
import { Bell, BellOff, DollarSign, TrendingDown, X, Check, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PriceAlertModal = ({ isOpen, onClose, card, token, onUpdate }) => {
  const [targetPrice, setTargetPrice] = useState(card?.target_price || '');
  const [alertOnAnyDrop, setAlertOnAnyDrop] = useState(card?.alert_on_any_drop || false);
  const [alertEnabled, setAlertEnabled] = useState(card?.alert_enabled !== false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen || !card) return null;

  const currentPrice = card.price;
  const suggestedPrices = [
    { label: '5% below', value: Math.round(currentPrice * 0.95 * 100) / 100 },
    { label: '10% below', value: Math.round(currentPrice * 0.90 * 100) / 100 },
    { label: '15% below', value: Math.round(currentPrice * 0.85 * 100) / 100 },
    { label: '20% below', value: Math.round(currentPrice * 0.80 * 100) / 100 },
  ];

  const handleSave = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/watchlist/${card.id}/alert`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          target_price: targetPrice ? parseFloat(targetPrice) : null,
          alert_on_any_drop: alertOnAnyDrop,
          alert_enabled: alertEnabled
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to update alert');
      }

      onUpdate && onUpdate();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500/20 rounded-lg">
              <Bell className="w-5 h-5 text-amber-400" />
            </div>
            <h2 className="text-lg font-bold text-white">Price Alert</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>

        <div className="p-4 space-y-5">
          {/* Card Info */}
          <div className="bg-zinc-800/50 rounded-lg p-3">
            <p className="text-sm text-zinc-400 mb-1">{card.year} {card.set_name}</p>
            <p className="font-semibold text-white">{card.player_name}</p>
            <p className="text-lg font-bold text-amber-400 mt-1">${currentPrice.toLocaleString()}</p>
          </div>

          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between p-3 bg-zinc-800/30 rounded-lg">
            <div className="flex items-center gap-2">
              {alertEnabled ? (
                <Bell className="w-5 h-5 text-amber-400" />
              ) : (
                <BellOff className="w-5 h-5 text-zinc-500" />
              )}
              <span className="text-white font-medium">Price Alerts</span>
            </div>
            <button
              onClick={() => setAlertEnabled(!alertEnabled)}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                alertEnabled ? 'bg-amber-500' : 'bg-zinc-700'
              }`}
            >
              <span
                className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                  alertEnabled ? 'left-7' : 'left-1'
                }`}
              />
            </button>
          </div>

          {alertEnabled && (
            <>
              {/* Target Price Input */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">
                  <DollarSign className="w-4 h-4 inline mr-1" />
                  Alert me when price drops to:
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                  <input
                    type="number"
                    value={targetPrice}
                    onChange={(e) => setTargetPrice(e.target.value)}
                    placeholder="Enter target price"
                    step="0.01"
                    min="0"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-8 pr-4 py-3 text-white placeholder:text-zinc-500 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  />
                </div>
                
                {/* Quick Select Buttons */}
                <div className="flex flex-wrap gap-2 mt-2">
                  {suggestedPrices.map((sp) => (
                    <button
                      key={sp.label}
                      onClick={() => setTargetPrice(sp.value)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                        parseFloat(targetPrice) === sp.value
                          ? 'bg-amber-500 text-black'
                          : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
                      }`}
                    >
                      {sp.label} (${sp.value})
                    </button>
                  ))}
                </div>
              </div>

              {/* Alert on Any Drop */}
              <div className="flex items-center justify-between p-3 bg-zinc-800/30 rounded-lg">
                <div className="flex items-center gap-2">
                  <TrendingDown className="w-5 h-5 text-emerald-400" />
                  <div>
                    <span className="text-white font-medium block">Alert on any significant drop</span>
                    <span className="text-xs text-zinc-500">Notify when price drops 5%+</span>
                  </div>
                </div>
                <button
                  onClick={() => setAlertOnAnyDrop(!alertOnAnyDrop)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    alertOnAnyDrop ? 'bg-emerald-500' : 'bg-zinc-700'
                  }`}
                >
                  <span
                    className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      alertOnAnyDrop ? 'left-7' : 'left-1'
                    }`}
                  />
                </button>
              </div>
            </>
          )}

          {/* Error */}
          {error && (
            <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Save Button */}
          <button
            onClick={handleSave}
            disabled={loading}
            className="w-full py-3 bg-amber-500 hover:bg-amber-400 disabled:bg-zinc-700 text-black font-bold rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check className="w-5 h-5" />
                Save Alert Settings
              </>
            )}
          </button>

          {/* Info Text */}
          <p className="text-xs text-zinc-500 text-center">
            You'll receive an email when this card's price meets your criteria.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PriceAlertModal;
